import type { ParseKeys } from 'i18next';
import type { StaffTier } from '@/admin/access';
import { hasTier } from '@/admin/access';
import type { components } from '@/api/qraft/schema';

type AdminPostCreate = components['schemas']['AdminPostCreate'];
type AdminPostTermCreate = components['schemas']['AdminPostTermCreate'];

/** The four kinds the editor handles; follow-up tutorials are not editable. */
export type EditablePostKind = AdminPostCreate['kind'];
export type EditableTaxonomy = AdminPostTermCreate['taxonomy'];

type KindInfo = {
  kind: EditablePostKind;
  taxonomy: EditableTaxonomy;
  /** Lowest tier the server lets edit this kind (`require_kind_tier`). */
  minTier: StaffTier;
  labelKey: ParseKeys;
  taxonomyLabelKey: ParseKeys;
};

// Order is the tab order. Study-helper kinds are front-end-support work;
// student-facing kinds need support, mirroring the server's tier rule.
export const POST_KINDS: readonly KindInfo[] = [
  {
    kind: 'study_helper_news',
    taxonomy: 'study_helper_news_category',
    minTier: 'FRONT_END_SUPPORT',
    labelKey: 'admin.publishing.kinds.study_helper_news',
    taxonomyLabelKey: 'admin.publishing.taxonomies.study_helper_news_category',
  },
  {
    kind: 'study_helper_tutorials',
    taxonomy: 'study_helper_tut_category',
    minTier: 'FRONT_END_SUPPORT',
    labelKey: 'admin.publishing.kinds.study_helper_tutorials',
    taxonomyLabelKey: 'admin.publishing.taxonomies.study_helper_tut_category',
  },
  {
    kind: 'student_news',
    taxonomy: 'student_news_category',
    minTier: 'SUPPORT',
    labelKey: 'admin.publishing.kinds.student_news',
    taxonomyLabelKey: 'admin.publishing.taxonomies.student_news_category',
  },
  {
    kind: 'student_tutorials',
    taxonomy: 'student_tutorials_category',
    minTier: 'SUPPORT',
    labelKey: 'admin.publishing.kinds.student_tutorials',
    taxonomyLabelKey: 'admin.publishing.taxonomies.student_tutorials_category',
  },
];

export function kindInfo(kind: EditablePostKind): KindInfo {
  return POST_KINDS.find((k) => k.kind === kind) ?? POST_KINDS[0];
}

type Viewer = Parameters<typeof hasTier>[0];

/** Kinds the viewer's tier may edit; the tab bar renders only these. */
export function visibleKinds(me: Viewer): KindInfo[] {
  return POST_KINDS.filter((k) => hasTier(me, k.minTier));
}

export function parseKind(raw: unknown): EditablePostKind | undefined {
  return POST_KINDS.find((k) => k.kind === raw)?.kind;
}

export function parseTaxonomy(raw: unknown): EditableTaxonomy | undefined {
  return POST_KINDS.find((k) => k.taxonomy === raw)?.taxonomy;
}

/** Only SH kinds notify helpers on first publish (server: `posts/service_admin.py`). */
export function notifiesHelpers(kind: EditablePostKind): boolean {
  return kind === 'study_helper_news' || kind === 'study_helper_tutorials';
}
